/**
 * Document parsers — File → raw text.
 *
 * All parsers are async and isomorphic: they work in a browser or a
 * web worker. PDF and DOCX parsers use dynamic imports so this module
 * can be imported in Node (e.g. for unit tests) without pulling in the
 * heavy pdfjs/mammoth dependencies unless actually used.
 *
 * Downstream: feed the resulting text into `parseResume` / `parseJobDescription`
 * from `./extractors.js` to get the `ParsedDocument` shape.
 */

export type DocumentKind = "pdf" | "docx" | "text";

export interface ParseResult {
  kind: DocumentKind;
  text: string;
  pageCount?: number;
  warnings?: string[];
  source?: string; // filename if available
}

// ---------------------------------------------------------------------------
// Plain text
// ---------------------------------------------------------------------------
export function parseText(text: string, source?: string): ParseResult {
  return {
    kind: "text",
    text: text.replace(/\r\n/g, "\n").trim(),
    source,
  };
}

// ---------------------------------------------------------------------------
// PDF via pdfjs-dist
// ---------------------------------------------------------------------------

// Worker URL is set by the host app exactly once via setPdfWorkerUrl.
// Host apps (apps/web) resolve the URL via Vite's `?url` transform at
// build time and pass the resulting static string here. Keeping this
// out of engine-ts means the engine stays framework-agnostic AND the
// host app can lazy-import pdfjs-dist without a second hard-coded path.
let pdfWorkerUrl: string | null = null;

/**
 * Set the URL for the pdfjs worker bundle. Must be called exactly once,
 * before the first `parsePdf` invocation. Idempotent.
 */
export function setPdfWorkerUrl(url: string): void {
  pdfWorkerUrl = url;
}

export async function parsePdf(
  input: ArrayBuffer | Uint8Array,
  source?: string,
): Promise<ParseResult> {
  const pdfjs = await import("pdfjs-dist");
  if (pdfWorkerUrl && !pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
  }
  const data = input instanceof Uint8Array ? input : new Uint8Array(input);
  const loadingTask = pdfjs.getDocument({ data });
  const pdf = await loadingTask.promise;

  const pageTexts: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const items = textContent.items as Array<{ str?: string }>;
    const pageText = items
      .map((it) => it.str ?? "")
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    pageTexts.push(pageText);
  }

  return {
    kind: "pdf",
    text: pageTexts.join("\n\n"),
    pageCount: pdf.numPages,
    source,
  };
}

// ---------------------------------------------------------------------------
// DOCX via mammoth
// ---------------------------------------------------------------------------
export async function parseDocx(
  input: ArrayBuffer,
  source?: string,
): Promise<ParseResult> {
  const mammoth = await import("mammoth/mammoth.browser.js");
  const result = await mammoth.extractRawText({ arrayBuffer: input });
  return {
    kind: "docx",
    text: (result.value ?? "").trim(),
    warnings: (result.messages ?? []).map(
      (m: { message?: string }) => m.message ?? "",
    ),
    source,
  };
}

// ---------------------------------------------------------------------------
// Smart dispatcher — pick parser by file extension / MIME type
// ---------------------------------------------------------------------------
export async function parseFile(file: File): Promise<ParseResult> {
  const name = file.name.toLowerCase();
  const mime = file.type.toLowerCase();

  if (mime === "application/pdf" || name.endsWith(".pdf")) {
    const buf = await file.arrayBuffer();
    return parsePdf(buf, file.name);
  }

  if (
    mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    name.endsWith(".docx")
  ) {
    const buf = await file.arrayBuffer();
    return parseDocx(buf, file.name);
  }

  if (
    mime === "text/plain" ||
    name.endsWith(".txt") ||
    name.endsWith(".md") ||
    name.endsWith(".markdown")
  ) {
    const text = await file.text();
    return parseText(text, file.name);
  }

  // Fallback: try as text. Many ATS exports are plain text with no extension.
  const text = await file.text();
  return parseText(text, file.name);
}
